import { observer } from 'mobx-react';
import React from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { Badge } from '@components/ui/badge';
import { useTabs } from '../../components/hooks/use-tabs';
import { PageHeader } from '../../components/page-header';
import { PageTabs } from '../../components/page-tabs';
import { Spinner } from '../../components/spinner';
import { CustomFacilitySearchDto } from '../../core/api/StudioClient';
import { injectTSDI } from '../../core/tsdi';
import { EmployeeData } from './employee-data';
import { FacilityEditStore } from './store';

const pageTabs = [
    { label: 'Details', id: 'details' },
    { label: 'Location', id: 'location' }
];

function statusVariant(
    status: CustomFacilitySearchDto['status']
): 'success' | 'destructive' | 'muted' {
    switch (status) {
        case 'VERIFIED':
            return 'success';
        case 'DELETED':
            return 'destructive';
        default:
            return 'muted';
    }
}

export const FacilityEdit = observer(() => {
    const { facility } = injectTSDI(FacilityEditStore);
    const { tab, getPageLink, setPageTab } = useTabs(pageTabs);
    const { pathname } = useLocation();
    const { facilityId } = useParams();

    const navigate = useNavigate();

    const changePage = (page: number) => {
        navigate(getPageLink(page));
    };

    React.useEffect(() => {
        setPageTab(pathname, 3);
    }, [pathname]);

    React.useEffect(() => {
        if (facilityId) {
            facility.request(facilityId);
        }
    }, [facilityId]);

    return facility.match({
        loading: () => (
            <div className="flex min-h-[300px] items-center justify-center">
                <Spinner />
            </div>
        ),
        fetched: (facility) => (
            <>
                <PageHeader
                    title={facility.internalData.name}
                    badges={
                        <>
                            <Badge
                                variant={statusVariant(
                                    facility.internalData.status
                                )}
                            >
                                {facility.internalData.status}
                            </Badge>
                            <Badge variant="outline">
                                {facility.internalData.facilityType}
                            </Badge>
                        </>
                    }
                    actions={<EmployeeData facility={facility} />}
                />
                <PageTabs tabs={pageTabs} value={tab} onChange={changePage} />
                <div className="mt-6">
                    <Outlet />
                </div>
            </>
        )
    });
});
